import { Truck, Package, ArrowDownToLine, Building, ShieldCheck, FileCheck2, Sparkles, Phone } from "lucide-react";
import { PHONE_DISPLAY, PHONE_LINK } from "@/lib/constants";

const items = [
  { icon: ArrowDownToLine, title: "Étages sans ascenseur", desc: "Nous descendons vos archives, peu importe l'étage." },
  { icon: Building, title: "Caves & sous-sols", desc: "Accès difficiles, locaux exigus : c'est notre quotidien." },
  { icon: Package, title: "Cartons lourds & en vrac", desc: "Emballage, étiquetage, palettisation si nécessaire." },
  { icon: Truck, title: "Chargement & transport", desc: "Véhicules dédiés, opérateurs habilités, traçabilité." },
];

export default function Differentiation() {
  return (
    <section
      id="service"
      className="py-20 md:py-32 bg-white"
      data-testid="differentiation-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-14">
          <div className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-blue-600">
            Notre vraie différence
          </div>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-950" data-testid="differentiation-title">
            La manutention complète, <span className="text-blue-600">incluse.</span>
          </h2>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            Contrairement à un simple broyeur, nous prenons en charge l'intégralité de la chaîne — du carton dans votre cave jusqu'au certificat signé.
          </p>
        </div>

        {/* Big highlight */}
        <div className="rounded-3xl overflow-hidden bg-slate-950 text-white p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center mb-14" data-testid="diff-highlight">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-500/15 px-3 py-1 text-xs font-bold text-blue-300 uppercase tracking-widest">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} />
              Soulagement logistique
            </div>
            <h3 className="font-display mt-5 text-3xl md:text-5xl font-extrabold tracking-tighter leading-[1.05]">
              Vos équipes<br/>ne portent <span className="text-blue-400">rien.</span>
            </h3>
            <p className="mt-4 text-slate-300 leading-relaxed max-w-md">
              On vient. On emballe. On descend. On charge. On part. Vous récupérez vos locaux, on vous remet un certificat. Point.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Zéro effort", "Zéro stress", "Zéro logistique"].map((t) => (
                <span key={t} className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-xs font-semibold">{t}</span>
              ))}
            </div>
            <a href={`tel:${PHONE_LINK}`} className="mt-8 inline-flex items-center gap-2 bg-blue-600 text-white font-bold py-3 px-5 rounded-full hover:bg-blue-700 transition-all" data-testid="diff-call-button">
              <Phone className="h-4 w-4" strokeWidth={2} />
              Appeler {PHONE_DISPLAY}
            </a>
          </div>
          <div className="relative rounded-2xl overflow-hidden h-64 md:h-80">
            <img
              src="https://customer-assets.emergentagent.com/job_archives-secure-paca/artifacts/r5p4laal_WhatsAppImage20251126at174605jpg_6951a4119856c.jpg"
              alt="Bennes pleines de cartons d'archives — intervention ArchivExpress sur site client"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        {/* Flat service items */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
          {items.map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              data-testid={`diff-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <Icon className="h-9 w-9 text-blue-600" strokeWidth={1.5} />
              <h3 className="font-display mt-4 text-lg font-bold text-slate-950 tracking-tight">{title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>

        {/* Certificate band — minimal */}
        <div className="mt-16 border-t border-slate-200 pt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
          <div className="flex items-center gap-4">
            <FileCheck2 className="h-8 w-8 text-blue-600 shrink-0" strokeWidth={1.5} />
            <div>
              <h3 className="font-display text-lg md:text-xl font-bold text-slate-950">Certificat de destruction inclus</h3>
              <p className="text-slate-600 text-sm mt-0.5">Preuve légale conforme RGPD remise après chaque intervention.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm font-bold text-blue-700">
            <ShieldCheck className="h-5 w-5" strokeWidth={1.75} />
            Conformité 100% garantie
          </div>
        </div>
      </div>
    </section>
  );
}
